import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ReactComponent as ClueyPlusLogo } from '../../assets/icons/cluey-plus-logo.svg';
import collapseSidebarIcon from '../../assets/icons/img-tab-paperclip-collapse.png';
import expandSidebarIcon from '../../assets/icons/img-tab-paperclip-expand.png';
import { useRevalidateAuthOnVisible } from '../../hooks/useRevalidateAuthOnVisible';
import { SidebarInset, SidebarProvider, SidebarTrigger, useSidebar } from '../components/ui/sidebar';
import { AppSidebar } from '../features/app-sidebar/AppSidebar';
import { getPageConfig, parseCurrentPage } from './page';
import { usePageHeader } from './usePageHeader';
import { usePageTitle } from './usePageTitle';

export function StickyHeader() {
  return (
    <div className="flex flex-row items-center gap-0 md:hidden">
      <SidebarTrigger className="md:hidden" />
      <ClueyPlusLogo className="h-5" />
    </div>
  );
}

// Renders the app-shell header content. Prefers the richer page-header (icon +
// breadcrumb) set from the route's page config; falls back to the plain title
// that not-yet-migrated pages still set via usePageTitle.
function PageHeader() {
  const { label, icon: Icon, showIcon, showBackButton, headerRight, onBackClick } = usePageHeader();
  const title = usePageTitle((state) => state.title);
  const history = useHistory();

  if (label) {
    return (
      <div className="flex flex-row items-center gap-4">
        {showBackButton ? (
          <button
            type="button"
            onClick={onBackClick ?? (() => history.goBack())}
            aria-label="Go back"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-brown-300"
          >
            <ArrowLeft className="h-6 w-6 shrink-0 text-orange-400" />
          </button>
        ) : (
          showIcon && Icon && <Icon className="h-6 w-6 shrink-0" />
        )}
        <h1 className="py-0 text-lg font-bold md:py-3 md:text-2xl">{label}</h1>
        {headerRight && <div className="ml-auto">{headerRight}</div>}
      </div>
    );
  }

  if (title) {
    return <h1 className="text-lg font-bold md:text-2xl">{title}</h1>;
  }

  return null;
}

function AppShellLayout({ children }: { children: React.ReactNode }) {
  const { open: sidebarOpen, toggleSidebar } = useSidebar();
  const { pathname } = useLocation();

  // Drive the header from the current route's page config. Pages without a
  // config reset to empty, so the header falls back to usePageTitle.
  React.useLayoutEffect(() => {
    // A page can claim the header via useSetPageHeader (e.g. the student profile's
    // dynamic "{name}'s profile"). That hook is a layout effect on a descendant,
    // so it runs before this one and sets `owned`; when owned, leave the header
    // alone rather than overwriting it with the static route config.
    if (usePageHeader.getState().owned) return;
    const { parent } = parseCurrentPage(pathname);
    const config = getPageConfig(parent);
    if (config) {
      usePageHeader.setState({
        label: config.label,
        icon: config.icon,
        showIcon: config.showIcon ?? false,
        showBackButton: config.showBackButton ?? false,
      });
    } else {
      usePageHeader.getState().resetHeader();
    }
  }, [pathname]);

  return (
    <>
      <AppSidebar />
      <div className="md:block">
        {sidebarOpen ? (
          <button className="absolute top-3 z-[11] -ml-5 cursor-pointer" onClick={toggleSidebar}>
            <img src={collapseSidebarIcon} alt="Collapse Sidebar" className="h-[75px]" />
          </button>
        ) : (
          <button className="absolute top-5 z-[11] cursor-pointer" onClick={toggleSidebar}>
            <img src={expandSidebarIcon} alt="Expand Sidebar" className="h-[75px]" />
          </button>
        )}
      </div>
      <SidebarInset className="flex h-screen flex-col overflow-hidden">
        <header className="sticky top-0 z-10 flex shrink-0 items-center gap-3 border-b border-grey-150 bg-background px-5 py-4 md:hidden md:px-10 md:py-4">
          <StickyHeader />
        </header>
        <div className="sticky top-0 z-10 flex flex-row items-center px-5 py-3 md:px-10 md:py-7">
          <PageHeader />

          <div className="h-9 md:h-12">{/* tutor connect component in the future?? */}</div>
        </div>
        {/* `brand-cta` opts every authenticated page into the brand button
            styling (solid / reverse / disabled) — see the .brand-cta rules in
            tailwind.css. Applied here at the shared content region rather than
            per page. */}
        <div
          data-main-scroll-container
          className="brand-cta flex min-h-0 flex-1 flex-col overflow-y-auto bg-orange-100"
        >
          {children}
        </div>
      </SidebarInset>
    </>
  );
}

// Global app shell: the sidebar + the page content region. Wrap authenticated
// pages with this (see withAppShell) so the sidebar replaces the old top nav.
export function AppShell({ children }: { children: React.ReactNode }) {
  useRevalidateAuthOnVisible();

  return (
    <SidebarProvider>
      <AppShellLayout>{children}</AppShellLayout>
    </SidebarProvider>
  );
}

// HOC form for composing the shell around a route component.
export default function withAppShell<P extends object>(WrappedComponent: React.ComponentType<P>) {
  const WithAppShell = (props: P) => (
    <AppShell>
      <WrappedComponent {...props} />
    </AppShell>
  );
  WithAppShell.displayName = `withAppShell(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return WithAppShell;
}
